/**
 * Newsletter API Functions
 * Handles communication with the newsletter backend
 */

import type {
  FooterNewsletterCategory,
  NewsletterSubscribeRequest,
  NewsletterSubscribeResponse,
} from './types';
import { API_ENDPOINTS, MAILCHIMP_GROUP_ID } from './constants';

/** Public read endpoint for the admin-managed footer newsletter categories. */
const FOOTER_CATEGORIES_ENDPOINT = '/api/newsletter-footer-categories';

/** Public read endpoint for the global newsletter master switch. */
const NEWSLETTER_SETTINGS_ENDPOINT = '/api/newsletter-settings';

/**
 * Fetch the global newsletter master switch.
 *
 * Defaults to `true` (enabled) on any failure so the storefront keeps showing the
 * newsletter signup points even if the backend is unavailable.
 *
 * @returns whether the newsletter is enabled (defaults to `true` on error)
 */
export async function fetchNewsletterEnabled(): Promise<boolean> {
  try {
    const response = await fetch(NEWSLETTER_SETTINGS_ENDPOINT);
    if (!response.ok) return true;
    const data = (await response.json()) as { enabled?: boolean };
    return data.enabled !== false;
  } catch (error) {
    console.warn('fetchNewsletterEnabled: failed to load', error);
    return true;
  }
}

/**
 * Fetch the admin-managed footer newsletter categories (enabled, in render order).
 *
 * Returns an empty array on any failure so callers can fall back to the hardcoded
 * defaults — the public footer must keep working even if the backend is unavailable.
 *
 * @returns ordered footer categories, or `[]` on error
 */
export async function fetchFooterNewsletterCategories(): Promise<FooterNewsletterCategory[]> {
  try {
    const response = await fetch(FOOTER_CATEGORIES_ENDPOINT);
    if (!response.ok) return [];
    const data = (await response.json()) as Array<{
      id?: string;
      label?: string;
      groupId?: string;
      codes?: number[];
    }>;
    return data
      .filter((c) => typeof c.label === 'string' && c.label.trim().length > 0)
      .map((c) => ({
        id: c.id ?? c.label ?? '',
        label: c.label as string,
        groupId: c.groupId && c.groupId.trim().length > 0 ? c.groupId : MAILCHIMP_GROUP_ID,
        codes: Array.isArray(c.codes) ? c.codes : [],
      }));
  } catch (error) {
    console.warn('fetchFooterNewsletterCategories: failed to load', error);
    return [];
  }
}

/**
 * Subscribe to the newsletter
 * @param data - Subscription form data
 * @returns API response with subscription status
 */
export async function subscribeToNewsletter(
  data: NewsletterSubscribeRequest
): Promise<NewsletterSubscribeResponse> {
  try {
    const response = await fetch(API_ENDPOINTS.subscribe, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        error: result.error || 'Κάτι πήγε στραβά. Παρακαλούμε δοκιμάστε ξανά.',
        field: result.field,
      };
    }

    return result;
  } catch (error) {
    console.error('Newsletter subscription error:', error);
    return {
      error: 'Σφάλμα σύνδεσης. Παρακαλούμε δοκιμάστε ξανά αργότερα.',
    };
  }
}

/**
 * Check if an email is already subscribed
 * This can be called for early validation in the form
 * @param email - Email to check
 * @returns Status of the email subscription
 */
export async function checkEmailStatus(
  email: string
): Promise<{ isSubscribed: boolean; isPending: boolean }> {
  try {
    const response = await fetch(`${API_ENDPOINTS.subscribe}/check?email=${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return { isSubscribed: false, isPending: false };
    }

    const result = await response.json();
    return {
      isSubscribed: result.alreadySubscribed || false,
      isPending: result.pendingVerification || false,
    };
  } catch (error) {
    console.error('Email check error:', error);
    return { isSubscribed: false, isPending: false };
  }
}
